import React, { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { Box, CircularProgress, Divider, Grid, Link, TextField, Typography } from '@mui/material';
import MDialog from '../../components/controls/MDialog';
import { formatLocalTime } from '../../components/ex_dayjs';
import type { AdditionalListingsIssue } from './types';

const R2_BASE = 'https://r2.devsoft.vn/';

interface DumpData {
    siteId?: string;
    pageType?: string;
    variant?: string;
    error?: string | null;
    extractionData?: unknown;
    diagnostics?: {
        url?: string;
        title?: string;
        timestamp?: string;
        userAgent?: string;
        pageHtml?: string;
    };
    screenshot?: string | null;
}

interface DetailProps {
    open: boolean;
    data: AdditionalListingsIssue | null;
    onClose: () => void;
}

const AdditionalListingsIssueDetail: React.FC<DetailProps> = ({ open, data, onClose }) => {
    const dataProvider = useDataProvider();
    const [dump, setDump] = useState<DumpData | null>(null);
    const [dumpLoading, setDumpLoading] = useState(false);
    const [dumpError, setDumpError] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !data?.dumpUrl) {
            setDump(null);
            setDumpError(null);
            return;
        }
        setDumpLoading(true);
        setDump(null);
        setDumpError(null);
        dataProvider.fetchR2(R2_BASE + data.dumpUrl)
            .then((response: { json: DumpData }) => {
                setDump(response.json);
            })
            .catch((err: unknown) => {
                setDumpError(err instanceof Error ? err.message : 'Failed to load dump');
            })
            .finally(() => setDumpLoading(false));
    }, [open, data?.dumpUrl, dataProvider]);

    if (!data) return null;

    return (
        <MDialog
            maxWidth="lg"
            open={open}
            handleClose={onClose}
            title={`Issue #${data.id} — ${data.siteId ?? ''}`}
            name="additional-listings-issue-detail"
        >
            <Grid container spacing={2}>
                {/* Row 1: identifiers */}
                <Grid size={3}>
                    <TextField label="ID" value={data.id ?? ''} fullWidth InputProps={{ readOnly: true }} size="small" />
                </Grid>
                <Grid size={3}>
                    <TextField label="SiteId" value={data.siteId ?? ''} fullWidth InputProps={{ readOnly: true }} size="small" />
                </Grid>
                <Grid size={3}>
                    <TextField label="Type" value={data.type ?? ''} fullWidth InputProps={{ readOnly: true }} size="small" />
                </Grid>
                <Grid size={3}>
                    <TextField label="PageType" value={data.pageType ?? ''} fullWidth InputProps={{ readOnly: true }} size="small" />
                </Grid>

                {/* Row 2: reporter info */}
                <Grid size={6}>
                    <TextField label="ReportedBy" value={data.reportedBy ?? ''} fullWidth InputProps={{ readOnly: true }} size="small" />
                </Grid>
                <Grid size={6}>
                    <TextField
                        label="ReportedAt"
                        value={data.reportedAt ? formatLocalTime(data.reportedAt) : ''}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        size="small"
                    />
                </Grid>

                {/* Row 3: error */}
                <Grid size={12}>
                    <TextField
                        label="ErrorMessage"
                        value={data.errorMessage ?? ''}
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        InputProps={{ readOnly: true }}
                        size="small"
                    />
                </Grid>

                {/* Row 4: links */}
                <Grid size={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>DumpUrl</Typography>
                    {data.dumpUrl ? (
                        <Link href={R2_BASE + data.dumpUrl} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                            {data.dumpUrl}
                        </Link>
                    ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                </Grid>
                <Grid size={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Url</Typography>
                    {data.url ? (
                        <Link href={data.url} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                            {data.url}
                        </Link>
                    ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                </Grid>

                {/* Dump section */}
                {data.dumpUrl && (
                    <Grid size={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" gutterBottom>Dump Data</Typography>

                        {dumpLoading && (
                            <Box display="flex" alignItems="center" gap={1} py={1}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">Loading dump…</Typography>
                            </Box>
                        )}

                        {dumpError && (
                            <Typography variant="body2" color="error">{dumpError}</Typography>
                        )}

                        {dump && (
                            <Grid container spacing={2}>
                                {/* Screenshot */}
                                {dump.screenshot && (
                                    <Grid size={12}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Screenshot</Typography>
                                        <Box
                                            component="img"
                                            src={dump.screenshot}
                                            alt="Page screenshot"
                                            sx={{
                                                width: '100%',
                                                maxHeight: 420,
                                                objectFit: 'contain',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                            }}
                                        />
                                    </Grid>
                                )}

                                {/* Diagnostics */}
                                {dump.diagnostics?.url && (
                                    <Grid size={12}>
                                        <TextField label="Page URL (at capture)" value={dump.diagnostics.url} fullWidth InputProps={{ readOnly: true }} size="small" />
                                    </Grid>
                                )}
                                {dump.diagnostics?.title && (
                                    <Grid size={8}>
                                        <TextField label="Page Title" value={dump.diagnostics.title} fullWidth InputProps={{ readOnly: true }} size="small" />
                                    </Grid>
                                )}
                                {dump.diagnostics?.timestamp && (
                                    <Grid size={4}>
                                        <TextField label="Captured At" value={formatLocalTime(dump.diagnostics.timestamp)} fullWidth InputProps={{ readOnly: true }} size="small" />
                                    </Grid>
                                )}

                                {/* Error from dump (may differ from errorMessage in the record) */}
                                {dump.error && (
                                    <Grid size={12}>
                                        <TextField
                                            label="Dump Error"
                                            value={String(dump.error)}
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            maxRows={5}
                                            InputProps={{ readOnly: true }}
                                            size="small"
                                        />
                                    </Grid>
                                )}

                                {/* Extraction data */}
                                {dump.extractionData !== null && dump.extractionData !== undefined && (
                                    <Grid size={12}>
                                        <TextField
                                            label="Extraction Data"
                                            value={typeof dump.extractionData === 'string'
                                                ? dump.extractionData
                                                : JSON.stringify(dump.extractionData, null, 2)}
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            maxRows={12}
                                            InputProps={{ readOnly: true }}
                                            size="small"
                                            sx={{ fontFamily: 'monospace' }}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                )}
            </Grid>
        </MDialog>
    );
};

export default AdditionalListingsIssueDetail;
